import { isReadyForPickupGroupStatus, isInSortingWarehouseGroupStatus } from './consolidate-shipment-status-groups.util';

export const VINTEDGO_PICKUP_WINDOW_DAYS = 7;
export const VINTEDGO_PICKUP_MISSING_STATUS_GRACE_DAYS = 1;
/** Count SOs in In sorting warehouse when elapsed days since entry (day 0) are > this value. */
export const SO_IN_WAREHOUSE_MORE_THAN_DAYS = 2;

export type SaleOrderStatusSource = {
    status?: number | string | null;
    latestOperationalStatusId?: number | string | null;
};

/** Latest operational status from sale_orders_status_histories only (ignore legacy sale_orders.status). */
export function getSaleOrderOperationalStatusId(
    order: SaleOrderStatusSource | null | undefined,
): number | null {
    if (!order) {
        return null;
    }
    const fromHistory = order.latestOperationalStatusId;
    if (fromHistory === undefined || fromHistory === null || fromHistory === '') {
        return null;
    }
    const normalized = Number(fromHistory);
    return Number.isNaN(normalized) ? null : normalized;
}

export type PickupCountdownStatusClass = 'pickup-green' | 'pickup-warning' | 'pickup-red' | '';

/** Ready for pickup group statuses — countdown starts from ready_for_pickup_date in DB. */
export function isReadyForPickupStatus(status: number | null | undefined): boolean {
    return isReadyForPickupGroupStatus(status);
}

export function isOrderReadyForPickup(order: SaleOrderStatusSource | null | undefined): boolean {
    return isReadyForPickupStatus(getSaleOrderOperationalStatusId(order));
}

export function calculatePickupDaysRemaining(
    readyForPickupDate: string | Date | null | undefined,
): number | null {
    if (!readyForPickupDate) {
        return null;
    }

    const pickupDate = new Date(readyForPickupDate);
    if (Number.isNaN(pickupDate.getTime())) {
        return null;
    }

    const now = new Date();
    const pickupDay = Date.UTC(pickupDate.getUTCFullYear(), pickupDate.getUTCMonth(), pickupDate.getUTCDate());
    const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const daysElapsed = Math.floor((today - pickupDay) / (24 * 60 * 60 * 1000));

    return VINTEDGO_PICKUP_WINDOW_DAYS - daysElapsed;
}

export function hasPickupMissingStatusAlert(input: {
    status: number | null | undefined;
    readyForPickupDate?: string | Date | null;
}): boolean {
    if (!isReadyForPickupStatus(input.status)) {
        return false;
    }

    const daysRemaining = calculatePickupDaysRemaining(input.readyForPickupDate);
    if (daysRemaining == null) {
        return false;
    }

    return daysRemaining <= -VINTEDGO_PICKUP_MISSING_STATUS_GRACE_DAYS;
}

export function getPickupCountdownStatusClass(
    status: number,
    readyForPickupDate?: string | Date | null,
): PickupCountdownStatusClass {
    if (!isReadyForPickupStatus(status)) {
        return '';
    }

    if (hasPickupMissingStatusAlert({ status, readyForPickupDate })) {
        return 'pickup-red';
    }

    const daysRemaining = calculatePickupDaysRemaining(readyForPickupDate);
    if (daysRemaining == null) {
        return '';
    }

    if (daysRemaining >= 1) {
        return 'pickup-green';
    }

    if (daysRemaining <= -1) {
        return 'pickup-red';
    }

    return 'pickup-warning';
}

export function formatPickupDaysRemaining(
    status: number,
    readyForPickupDate?: string | Date | null,
): string {
    if (!isReadyForPickupStatus(status)) {
        return '';
    }

    if (hasPickupMissingStatusAlert({ status, readyForPickupDate })) {
        return 'Missing status';
    }

    const daysRemaining = calculatePickupDaysRemaining(readyForPickupDate);
    if (daysRemaining == null) {
        return '';
    }

    if (daysRemaining === 1) {
        return '1 day left';
    }

    if (daysRemaining === -1) {
        return '1 day overdue';
    }

    if (daysRemaining > 1) {
        return `${daysRemaining} days left`;
    }

    if (daysRemaining < -1) {
        return `${Math.abs(daysRemaining)} days overdue`;
    }

    return 'Last day';
}

export function countReadyForPickupOrders(orders: SaleOrderStatusSource[] | null | undefined): number {
    return (orders ?? []).filter((order) => isOrderReadyForPickup(order)).length;
}

export function isPickupMissed(order: {
    status?: number | string | null;
    latestOperationalStatusId?: number | string | null;
    readyForPickupDate?: string | Date | null;
}): boolean {
    const status = getSaleOrderOperationalStatusId(order);
    if (!isReadyForPickupStatus(status)) {
        return false;
    }

    if (hasPickupMissingStatusAlert({
        status,
        readyForPickupDate: order.readyForPickupDate,
    })) {
        return true;
    }

    const daysRemaining = calculatePickupDaysRemaining(order.readyForPickupDate);
    return daysRemaining != null && daysRemaining < 0;
}

export function isPickupWithinWindow(order: {
    status?: number | string | null;
    latestOperationalStatusId?: number | string | null;
    readyForPickupDate?: string | Date | null;
}): boolean {
    return isOrderReadyForPickup(order) && !isPickupMissed(order);
}

export function countPickupWithinWindow(
    orders: Array<{ status?: number; readyForPickupDate?: string | Date | null }> | null | undefined,
): number {
    return (orders ?? []).filter((order) => isPickupWithinWindow(order)).length;
}

export function countPickupMissed(
    orders: Array<{ status?: number; readyForPickupDate?: string | Date | null }> | null | undefined,
): number {
    return (orders ?? []).filter((order) => isPickupMissed(order)).length;
}

/**
 * Inclusive calendar days since date using the browser's local day boundaries
 * (matches how deliveryDate is shown in the UI as DD/MM/YYYY).
 * Delivery day itself = 0 elapsed → callers add 1 when they want “day 1”.
 */
export function calculateDaysSinceDate(
    dateValue: string | Date | null | undefined,
): number | null {
    if (!dateValue) {
        return null;
    }

    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) {
        return null;
    }

    const now = new Date();
    const startDay = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return Math.floor((today - startDay) / (24 * 60 * 60 * 1000));
}

/**
 * True when SO latest operational status is In sorting warehouse and elapsed
 * calendar days since streak start is strictly greater than `thresholdDays`
 * (entry day = 0; e.g. entered Mon → counts from Thu when threshold=2).
 */
export function isSoInWarehouseMoreThanDays(order: {
    status?: number | string | null;
    latestOperationalStatusId?: number | string | null;
    inSortingWarehouseSince?: string | Date | null;
    /** @deprecated unused — kept for call-site compatibility */
    deliveryDate?: string | Date | null;
}, thresholdDays: number = SO_IN_WAREHOUSE_MORE_THAN_DAYS): boolean {
    const operationalStatus = getSaleOrderOperationalStatusId(order);
    if (!isInSortingWarehouseGroupStatus(operationalStatus)) {
        return false;
    }

    const daysSinceEntered = calculateDaysSinceDate(order.inSortingWarehouseSince);
    return daysSinceEntered != null && daysSinceEntered > thresholdDays;
}

/**
 * Elapsed calendar days in the current In sorting warehouse streak
 * (entry day = 0). Returns null when not currently in that group or date missing.
 */
export function getDaysInWarehouse(order: {
    status?: number | string | null;
    latestOperationalStatusId?: number | string | null;
    inSortingWarehouseSince?: string | Date | null;
    /** @deprecated unused — kept for call-site compatibility */
    deliveryDate?: string | Date | null;
}): number | null {
    const operationalStatus = getSaleOrderOperationalStatusId(order);
    if (!isInSortingWarehouseGroupStatus(operationalStatus)) {
        return null;
    }

    const daysSinceEntered = calculateDaysSinceDate(order.inSortingWarehouseSince);
    if (daysSinceEntered == null || daysSinceEntered < 0) {
        return null;
    }

    return daysSinceEntered;
}

/** @deprecated Use getDaysInWarehouse */
export function getDaysInDeliveredStatus(order: {
    status?: number | string | null;
    latestOperationalStatusId?: number | string | null;
    inSortingWarehouseSince?: string | Date | null;
    deliveryDate?: string | Date | null;
}): number | null {
    return getDaysInWarehouse(order);
}

export function countSoInWarehouseMoreThanDays(
    orders: Array<{
        status?: number | string | null;
        latestOperationalStatusId?: number | string | null;
        inSortingWarehouseSince?: string | Date | null;
        deliveryDate?: string | Date | null;
    }> | null | undefined,
    thresholdDays: number = SO_IN_WAREHOUSE_MORE_THAN_DAYS,
): number {
    return (orders ?? []).filter((order) => isSoInWarehouseMoreThanDays(order, thresholdDays)).length;
}
